import React, { createRef, useCallback, useContext, useEffect, useState } from 'react';
import {
  CTA,
  styled,
  Text,
  TokenTextAppearance,
  TextTag,
  Breakpoints,
} from '@volkswagen-onehub/components-core';
import {useLocation, useNavigate} from 'react-router-dom';
import {getSearchButtonCopy} from '../utils/get-search-button-copy';
import { validatePostalCode } from '../UserLocation/utils';
import { useFeatureServices } from '../use-feature-services';
import { createConfiguration, createConstantData, createPartner, createProcess } from '../utils/get-tracking-event-data';
import { getSingleSelectedDealer } from '../utils/get-single-selected-dealer';
import { UserLocationContext } from '../UserLocation/UserLocationContext';
import { meetsBreakpoint } from '@vw-marketing/vwca-utils';
import { useAppliedFilterStore } from '../store/filtersv2';
import { useFiltering } from '../utils/use-filtering';

const ButtonWrapper = styled.div<{fixButton: boolean}>`
  margin: 0;
  width: 100%;
  padding: var(--size-dynamic0100) var(--size-grid002);
  display: grid;
  justify-content: center;
  transition: all 200ms ease-out;

  ${({fixButton}) => fixButton ? `
    position: fixed;
    bottom: 0;
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.1) 0px -6px 30px, rgba(0, 0, 0, 0.2) 0px -4px 8px;
  ` : null }
`;

interface Props {
  sectionHeight?: number;
}
export const SearchButton = ({sectionHeight = 0}: Props) => {

  const buttonRef = createRef<HTMLDivElement>();

  const [ fixButton, setFixButton ] = useState(true);

  const { applied } = useAppliedFilterStore();
  const { applySelectedFilters } = useFiltering();

  const { tracking } = useFeatureServices();
  const { postalCode } = useContext(UserLocationContext);

  const { getQueryParams } = useFiltering();
  const { model: models } = applied;

  const disabled = !(models && models?.length > 0) || !validatePostalCode(postalCode as string);

  let navigate = useNavigate();


  const button = getSearchButtonCopy();

  const partner = getSingleSelectedDealer();

  const location = useLocation();

  const onSearch = (e: React.SyntheticEvent<HTMLButtonElement>) => {

    if(validatePostalCode(postalCode as string)) {
      applySelectedFilters();
      const queries = getQueryParams("url", {returnString: true, useApplied: true});
      navigate(`/results?${queries}`, {state: {from: location.pathname}});
    }

    tracking.track('VWBasic_Button_Click', {
      ...createConstantData(),
      configuration: createConfiguration(),
      partner: createPartner(partner),
      process: createProcess("1B"),
      link: {
        url: e.currentTarget.formAction,
        name: e.currentTarget.innerText
      }
    });
  };

  const topBarHeight = meetsBreakpoint(Breakpoints.b560) ? 64 : 56;

  const handleScroll = useCallback(() => {
    if (buttonRef?.current) {
      const yPosition = window.scrollY + window.innerHeight;
      const buttonContainerHeight = buttonRef?.current.offsetHeight;
      const yTrigger = sectionHeight + buttonContainerHeight + topBarHeight;
      if (yPosition > yTrigger && fixButton) {
        setFixButton(false);
      } else if (yPosition < yTrigger && !fixButton) {
        setFixButton(true);
      }
    }
  }, [buttonRef]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <ButtonWrapper ref={buttonRef} fixButton={fixButton}>
      <CTA tag="button" disabled={disabled} onClick={onSearch}>
        <Text
          className="count"
          tag={TextTag.p}
          appearance={TokenTextAppearance.label200}
        >
          {button.label}
        </Text>
      </CTA>
    </ButtonWrapper>
  );
};
